"use client";

import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { getImageProps } from "next/image";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { ArrowLeft, ArrowRight, ArrowUpRight } from "lucide-react";
import { BookCall } from "@/components/booking/BookCall";
import { buttonVariants } from "@/components/ui/button";
import { Accent, SectionHeader } from "@/components/ui/SectionHeader";
import { ClientCarousel, type CarouselHandle, type CarouselItem } from "@/components/work/ClientCarousel";
import { ClientSheet } from "@/components/work/ClientSheet";
import { DELIVERABLE_ICON } from "@/components/work/ClientView";
import { cn } from "@/lib/utils";
import type { Client } from "@/lib/work";

const EASE = [0.16, 1, 0.3, 1] as const;
const pad = (n: number) => String(n).padStart(2, "0");

/** Card geometry: 4:3, which suits a site screenshot and still holds a portrait board. */
const CARD_W = 560;
const CARD_H = 420;

/**
 * The ring holds at most this many clients. Past it, cards would shrink and the ring would
 * read as a blur, so the rest are reached through the index under it, which lists everyone.
 */
const RING_MAX = 12;

/**
 * How much of the ring has to fit the frame, in card steps either side of the front. Tying
 * it to the step rather than to degrees keeps the cards the same size whatever the count:
 * on a wide screen about three cards show across, on a phone one card and the edges of
 * its neighbours.
 */
const REACH_STEPS = { wide: 1.4, narrow: 0.12 };

const NARROW = "(max-width: 767px)";
const subscribeNarrow = (onChange: () => void) => {
  const mq = window.matchMedia(NARROW);
  mq.addEventListener("change", onChange);
  return () => mq.removeEventListener("change", onChange);
};
const useNarrow = () => useSyncExternalStore(subscribeNarrow, () => window.matchMedia(NARROW).matches, () => false);

/**
 * The face of a client with nothing to screenshot (work shown in the studio's own
 * branding): the one dark surface the brand allows here, a mint grid, and the name.
 */
const BrandFace = ({ client }: { client: Client }) => (
  <div
    className="flex size-full flex-col justify-between bg-brand-navy p-7 text-white"
    style={{
      backgroundImage:
        "linear-gradient(rgba(43,196,138,0.14) 1px, transparent 1px), linear-gradient(90deg, rgba(43,196,138,0.14) 1px, transparent 1px)",
      backgroundSize: "28px 28px",
    }}
  >
    <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-brand-mint">
      {client.deliverables[0]?.label}
    </span>
    <span className="font-heading text-[2.6rem] uppercase leading-[0.86] tracking-tight">{client.name}</span>
  </div>
);

/**
 * What we do, proved by who we did it for.
 *
 * One card per client on a 3D ring (React Bits' CircularCarousel, forked in
 * ClientCarousel.tsx): it drifts on its own, turns under a drag, a swipe, a sideways
 * trackpad scroll or the arrow keys, and the front card opens the client. Under it, the
 * front client's name, line and deliverables change with the ring, so the section reads as
 * an index as well as a show.
 *
 * Opening a client raises its sheet over the section and puts /work/<slug> in the address
 * bar, so the back button closes it and the URL can be shared; loaded directly, that URL is
 * the client's own page. #work (the nav) and old #services links both land here.
 */
export const Portfolio = ({ clients }: { clients: Client[] }) => {
  const reduce = useReducedMotion();
  const narrow = useNarrow();
  const ring = useRef<CarouselHandle>(null);
  const [active, setActive] = useState(0);
  const [open, setOpen] = useState<number | null>(null);
  const pushed = useRef(false);

  const ringClients = clients.slice(0, RING_MAX);
  const step = 360 / Math.max(ringClients.length, 3);

  const items = useMemo<CarouselItem[]>(
    () =>
      clients.slice(0, RING_MAX).map((client) => ({
        key: client.slug,
        label: client.name,
        // An optimised URL from the image endpoint: about 1080px wide, the card at 2x once
        // the ring has been scaled to fit.
        src: client.cover
          ? getImageProps({ src: client.cover.src, alt: "", width: 480, height: 360 }).props.src
          : undefined,
        position: "50% 0%",
        face: client.cover ? undefined : <BrandFace client={client} />,
      })),
    [clients],
  );

  /* ── The sheet, and the address bar ────────────────────────────────────── */
  const show = useCallback(
    (i: number) => {
      setOpen(i);
      const url = `/work/${clients[i].slug}`;
      if (pushed.current) window.history.replaceState(window.history.state, "", url);
      else {
        window.history.pushState(window.history.state, "", url);
        pushed.current = true;
      }
    },
    [clients],
  );

  const close = useCallback(() => {
    setOpen(null);
    if (pushed.current) {
      pushed.current = false;
      window.history.back();
    }
  }, []);

  useEffect(() => {
    const onPop = () => {
      const slug = window.location.pathname.match(/^\/work\/([^/]+)/)?.[1];
      const index = slug ? clients.findIndex((c) => c.slug === slug) : -1;
      pushed.current = index >= 0;
      setOpen(index >= 0 ? index : null);
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [clients]);

  if (clients.length === 0) return null;
  const current = ringClients[active] ?? clients[0];

  return (
    <section id="work" aria-labelledby="work-heading" className="relative overflow-hidden bg-white py-16 sm:py-24 md:py-32">
      {/* Old links to #services still resolve here; the nav no longer uses it. */}
      <span id="services" aria-hidden className="absolute top-0" />

      <div className="mx-auto max-w-[95vw] px-4 sm:max-w-[90vw] sm:px-6 md:max-w-[1400px] md:px-8">
        <SectionHeader
          headingId="work-heading"
          title={
            <>
              What we have <Accent>shipped.</Accent>
            </>
          }
        />
      </div>

      <div className="relative mt-4 h-[clamp(300px,40vw,540px)] sm:mt-8">
        <ClientCarousel
          ref={ring}
          items={items}
          cardWidth={CARD_W}
          aspectRatio={CARD_W / CARD_H}
          reach={Math.min(180, step * (narrow ? REACH_STEPS.narrow : REACH_STEPS.wide))}
          reduced={Boolean(reduce)}
          label="Clients"
          onChange={setActive}
          onOpen={show}
        />
      </div>

      <div className="mx-auto mt-6 max-w-[95vw] px-4 sm:mt-10 sm:max-w-[90vw] sm:px-6 md:max-w-[1400px] md:px-8">
        <div className="grid gap-6 border-t border-black/10 pt-6 md:grid-cols-[minmax(0,1fr)_auto] md:items-start md:gap-10">
          <div aria-live="polite" className="min-h-[9.5rem] sm:min-h-[8rem]">
            <AnimatePresence mode="wait" initial={false}>
              <motion.div
                key={current.slug}
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: reduce ? 0 : 0.35, ease: EASE }}
              >
                <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-brand-accent">
                  {current.services.map((s) => s.title).join(" · ")}
                </p>
                <h3 className="mt-3 font-heading text-[clamp(2rem,4.4vw,3.5rem)] uppercase leading-[0.88] tracking-tight text-brand-ink">
                  {current.name}
                </h3>
                <p className="mt-3 max-w-2xl text-base leading-relaxed text-brand-ink/65 sm:text-lg">{current.blurb}</p>
                <ul aria-label="What we did" className="mt-4 flex flex-wrap gap-1.5">
                  {current.deliverables.map((d) => {
                    const Icon = DELIVERABLE_ICON[d.kind];
                    return (
                      <li
                        key={d.id}
                        className="inline-flex items-center gap-1.5 rounded-btn border border-black/10 px-2 py-1 font-mono text-[10px] uppercase tracking-[0.16em] text-brand-ink/60"
                      >
                        <Icon className="size-3" aria-hidden />
                        {d.label}
                      </li>
                    );
                  })}
                </ul>
              </motion.div>
            </AnimatePresence>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              aria-label="Previous client"
              onClick={() => ring.current?.step(-1)}
              className="grid size-12 place-items-center rounded-btn border border-black/15 text-brand-ink transition-colors duration-300 hover:border-brand-accent hover:text-brand-accent focus-visible:ring-2 focus-visible:ring-brand-accent"
            >
              <ArrowLeft className="size-4" />
            </button>
            <button
              type="button"
              aria-label="Next client"
              onClick={() => ring.current?.step(1)}
              className="grid size-12 place-items-center rounded-btn border border-black/15 text-brand-ink transition-colors duration-300 hover:border-brand-accent hover:text-brand-accent focus-visible:ring-2 focus-visible:ring-brand-accent"
            >
              <ArrowRight className="size-4" />
            </button>
            <a
              href={`/work/${current.slug}`}
              onClick={(event) => {
                if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
                event.preventDefault();
                show(active);
              }}
              className={cn(buttonVariants({ size: "lg" }), "ml-2")}
            >
              Open {current.name}
              <ArrowUpRight className="size-4" />
            </a>
          </div>
        </div>

        {/* Every client as a plain link: the ring's index for scanning, for keyboards and
            for crawlers, and the only way to the ones past RING_MAX. */}
        <nav aria-label="All clients" className="mt-14 sm:mt-16">
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-brand-ink/60">All clients</p>
          <ul className="mt-4 grid grid-cols-1 border-t border-black/10 sm:grid-cols-2 lg:grid-cols-4">
            {clients.map((client, i) => (
              <li key={client.slug} className="border-b border-black/10 sm:odd:border-r lg:border-r lg:[&:nth-child(4n)]:border-r-0">
                <a
                  href={`/work/${client.slug}`}
                  onClick={(event) => {
                    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
                    event.preventDefault();
                    if (i < RING_MAX) ring.current?.focus(i);
                    show(i);
                  }}
                  className="group flex items-center justify-between gap-3 px-1 py-3.5 outline-none transition-colors duration-300 hover:text-brand-accent focus-visible:ring-2 focus-visible:ring-brand-accent sm:px-4"
                >
                  <span className="flex min-w-0 items-baseline gap-3">
                    <span className="font-mono text-[10px] tracking-[0.2em] text-brand-ink/60">{pad(i + 1)}</span>
                    <span className="truncate font-semibold">{client.name}</span>
                  </span>
                  <span className="flex shrink-0 items-center gap-1.5 text-brand-ink/60 group-hover:text-brand-accent">
                    {client.deliverables.map((d) => {
                      const Icon = DELIVERABLE_ICON[d.kind];
                      return <Icon key={d.id} className="size-3.5" aria-label={d.label} />;
                    })}
                  </span>
                </a>
              </li>
            ))}
          </ul>
        </nav>

        <div className="mt-16 flex flex-col items-start gap-5 border-t border-black/10 pt-8 sm:mt-20 sm:flex-row sm:items-center sm:justify-between">
          <p className="font-heading text-2xl uppercase leading-[0.95] text-brand-ink sm:text-3xl">
            Yours could be <Accent>next.</Accent>
          </p>
          <BookCall className={cn(buttonVariants({ size: "lg" }))}>
            <span>Make yours next</span>
            <ArrowUpRight className="size-4" />
          </BookCall>
        </div>
      </div>

      <ClientSheet clients={clients} index={open} onClose={close} onIndexChange={show} />
    </section>
  );
};
